'use strict';

// external imports
import omniclone from 'omniclone';
import {keys} from 'ts-transformer-keys';
import {encode as encodeHtmlEntities} from 'html-entities';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import Status from '~/shared/src/mm/Status';
import Item from '~/shared/src/ci/Item';
import ReferenceProperty, {
    ReferencePropertyAttributes,
    ReferencePropertyJsonAttributes,
    ReferencePropertyAttributesForCreation,
    ReferencePropertyAttributesForUpdate,
    ReferencePropertyAttributesSettable,
    isReferencePropertyAttributes,
    isReferencePropertyJsonAttributes
} from '~/shared/src/ci/ReferenceProperty';
import EnumReferenceProperty, {
    EnumReferencePropertyAttributes,
    EnumReferencePropertyJsonAttributes,
    EnumReferencePropertyAttributesForCreation,
    EnumReferencePropertyAttributesForUpdate,
    EnumReferencePropertyAttributesSettable,
    isEnumReferencePropertyAttributes,
    isEnumReferencePropertyJsonAttributes
} from '~/shared/src/ci/EnumReferenceProperty';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {
    convertToDate,
    convertValueToString
} from '~/shared/src/utilities/valueConverters';
import {
    ItemMetaDataMapping,
    ReferenceMetaDataMapping,
    ReferencePropertyMetaDataMapping
} from '~/shared/src/utilities/modules/data';
import StackError from '~/shared/src/base/StackError';

// backend imports
///////////////////////////
////////////////////////////////////////////////////
//////////


// type definitions
export type ReferenceAttributes = {
    id? : number,
    referenceSetId : number,
    statusId? : number,
    srcItemId : number,
    destItemId : number,
    modifierUserId? : string,
    modificationTime? : string | NDate,
    validatorUserId? : string,
    validationTime? : string | NDate,
    validFromTime? : string | NDate,
    validToTime? : string | NDate,
    properties? : ReferencePropertyAttributes[],
    enumProperties? : EnumReferencePropertyAttributes[],
};
export const referenceAttributes = keys<ReferenceAttributes>() as string[];
export const isReferenceAttribute = ( attribute : string ) : attribute is ( keyof ReferenceAttributes ) => {
    return referenceAttributes.includes( attribute );
}
export const isReferenceAttributes = ( referenceAttributesToTest : any ) : referenceAttributesToTest is ReferenceAttributes => {
    let retVal = false;

    try {
        if ( typeof referenceAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( referenceAttributesToTest.id !== undefined && ( typeof referenceAttributesToTest.id !== 'number' || referenceAttributesToTest.id < 1 ) ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.referenceSetId === undefined || typeof referenceAttributesToTest.referenceSetId !== 'number' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.statusId !== undefined && typeof referenceAttributesToTest.statusId !== 'number' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.srcItemId === undefined || typeof referenceAttributesToTest.srcItemId !== 'number' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.destItemId === undefined || typeof referenceAttributesToTest.destItemId !== 'number' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.modifierUserId !== undefined && typeof referenceAttributesToTest.modifierUserId !== 'string' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.modificationTime !== undefined && typeof referenceAttributesToTest.modificationTime !== 'string' && !( referenceAttributesToTest.modificationTime instanceof Date ) && referenceAttributesToTest.modificationTime !== 'null' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.validatorUserId !== undefined && typeof referenceAttributesToTest.validatorUserId !== 'string' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.validationTime !== undefined && typeof referenceAttributesToTest.validationTime !== 'string' && !( referenceAttributesToTest.validationTime instanceof Date ) && referenceAttributesToTest.validationTime !== 'null' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.validFromTime !== undefined && typeof referenceAttributesToTest.validFromTime !== 'string' && !( referenceAttributesToTest.validFromTime instanceof Date ) && referenceAttributesToTest.validFromTime !== 'null' ) {
            throw new Error();
        }
        if ( referenceAttributesToTest.validToTime !== undefined && typeof referenceAttributesToTest.validToTime !== 'string' && !( referenceAttributesToTest.validToTime instanceof Date ) && referenceAttributesToTest.validToTime !== 'null' ) {
            throw new Error();
        }

        if ( referenceAttributesToTest.properties !== undefined ) {
            if ( !Array.isArray( referenceAttributesToTest.properties as any ) ) {
                throw new Error();
            }

            for ( const propertyAttributes of referenceAttributesToTest.properties ) {
                if ( !isReferencePropertyAttributes( { referenceId: 1, ...propertyAttributes } as any ) ) {
                    throw new Error();
                }
            }
        }

        if ( referenceAttributesToTest.enumProperties !== undefined ) {
            if ( !Array.isArray( referenceAttributesToTest.enumProperties as any ) ) {
                throw new Error();
            }

            for ( const propertyAttributes of referenceAttributesToTest.enumProperties ) {
                if ( !isEnumReferencePropertyAttributes( { referenceId: 1, ...propertyAttributes } as any ) ) {
                    throw new Error();
                }
            }
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type ReferenceOnlyJsonAttributes = Omit<ReferenceAttributes, 'properties' | 'enumProperties'>
    & Required<Pick<ReferenceAttributes, 'id'>>;
export type ReferenceJsonAttributes = ReferenceOnlyJsonAttributes
    & { properties? : ReferencePropertyJsonAttributes[] }
    & { enumProperties? : EnumReferencePropertyJsonAttributes[] };
export const isReferenceJsonAttributes = ( referenceJsonAttributesToTest : any ) : referenceJsonAttributesToTest is ReferenceJsonAttributes => {
    let retVal = false;

    try {
        if ( typeof referenceJsonAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( referenceJsonAttributesToTest.id === undefined || typeof referenceJsonAttributesToTest.id !== 'number' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.referenceSetId === undefined || typeof referenceJsonAttributesToTest.referenceSetId !== 'number' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.statusId !== undefined && typeof referenceJsonAttributesToTest.statusId !== 'number' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.srcItemId === undefined || typeof referenceJsonAttributesToTest.srcItemId !== 'number' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.destItemId === undefined || typeof referenceJsonAttributesToTest.destItemId !== 'number' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.modifierUserId !== undefined && typeof referenceJsonAttributesToTest.modifierUserId !== 'string' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.modificationTime !== undefined && typeof referenceJsonAttributesToTest.modificationTime !== 'string' && !( referenceJsonAttributesToTest.modificationTime instanceof Date ) && referenceJsonAttributesToTest.modificationTime !== 'null' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.validatorUserId !== undefined && typeof referenceJsonAttributesToTest.validatorUserId !== 'string' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.validationTime !== undefined && typeof referenceJsonAttributesToTest.validationTime !== 'string' && !( referenceJsonAttributesToTest.validationTime instanceof Date ) && referenceJsonAttributesToTest.validationTime !== 'null' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.validFromTime !== undefined && typeof referenceJsonAttributesToTest.validFromTime !== 'string' && !( referenceJsonAttributesToTest.validFromTime instanceof Date ) && referenceJsonAttributesToTest.validFromTime !== 'null' ) {
            throw new Error();
        }
        if ( referenceJsonAttributesToTest.validToTime !== undefined && typeof referenceJsonAttributesToTest.validToTime !== 'string' && !( referenceJsonAttributesToTest.validToTime instanceof Date ) && referenceJsonAttributesToTest.validToTime !== 'null' ) {
            throw new Error();
        }

        if ( referenceJsonAttributesToTest.properties !== undefined ) {
            if ( !Array.isArray( referenceJsonAttributesToTest.properties as any ) ) {
                throw new Error();
            }

            for ( const propertyJsonAttributes of referenceJsonAttributesToTest.properties ) {
                if ( !isReferencePropertyJsonAttributes( propertyJsonAttributes ) ) {
                    throw new Error();
                }
            }
        }

        if ( referenceJsonAttributesToTest.enumProperties !== undefined ) {
            if ( !Array.isArray( referenceJsonAttributesToTest.enumProperties as any ) ) {
                throw new Error();
            }

            for ( const enumPropertyJsonAttributes of referenceJsonAttributesToTest.enumProperties ) {
                if ( !isEnumReferencePropertyJsonAttributes( enumPropertyJsonAttributes ) ) {
                    throw new Error();
                }
            }
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type ReferenceOnlyAttributesSettable = Partial<Pick<ReferenceAttributes, 'statusId'>>;
export const referenceOnlyAttributesSettable = keys<ReferenceOnlyAttributesSettable>() as string[];
export type ReferenceAttributesSettable = ReferenceOnlyAttributesSettable
    & { properties? : { [ propertyTypeId : string ] : ReferencePropertyAttributesSettable } }
    & { enumProperties? : { [ propertyTypeId : string ] : EnumReferencePropertyAttributesSettable } };

export type ReferenceOnlyAttributesForCreation = Required<Pick<ReferenceAttributes, 'referenceSetId' | 'statusId' | 'srcItemId' | 'destItemId'>>;
export const referenceOnlyAttributesForCreation = keys<ReferenceOnlyAttributesForCreation>() as string[];
export type ReferenceAttributesForCreation = ReferenceOnlyAttributesForCreation
    & { properties? : ReferencePropertyAttributesForCreation[] }
    & { enumProperties? : EnumReferencePropertyAttributesForCreation[] };
export const referenceAttributesForCreation = keys<ReferenceAttributesForCreation>() as string[];

export type ReferenceOnlyAttributesForUpdate = Required<Pick<ReferenceAttributes, 'id'>>
    & ReferenceOnlyAttributesSettable;
export type ReferenceAttributesForUpdate = ReferenceOnlyAttributesForUpdate
    & { properties? : ( ReferencePropertyAttributesForUpdate | ReferenceAttributesForCreation )[] }
    & { enumProperties? : ( EnumReferencePropertyAttributesForUpdate | EnumReferencePropertyAttributesForCreation )[] };

export type ReferenceCollection = { [ referenceId : number ] : Reference };

/**
 * The Reference class.
 */
class Reference {

    /**
     * The unique Id counter for References
     */
    static uniqueIdCount = -1;


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the Reference
     */
    protected _id : number;
    get id() {
        return this._id;
    }

    /**
     * The ReferenceSetID of the Reference
     */
    readonly referenceSetId : number;

    /**
     * The StatusID of the Reference.
     */
    protected _statusId : number;
    get statusId() : number {
        return this._statusId;
    }

    set statusId( value : number ) {
        this.validateStatusId( value );

        this.handleModified( 'statusId', value );

        this._statusId = value;
    }

    /**
     * The id of the source Item of the Reference
     */
    protected _srcItemId : number;
    get srcItemId() : number {
        return this._srcItemId;
    }

    /**
     * The id of the destination Item of the Reference
     */
    protected _destItemId : number;
    get destItemId() : number {
        return this._destItemId;
    }

    /**
     * The UserID of the last modifier of the Reference
     */
    protected _modifierUserId : string = '';
    get modifierUserId() : string {
        return this._modifierUserId;
    }

    /**
     * The last modification date of the Reference
     */
    protected _modificationTime : NDate = null;
    get modificationTime() : NDate {
        return this._modificationTime;
    }

    /**
     * The UserID of the validator of the Reference
     */
    protected _validatorUserId : string = '';
    get validatorUserId() : string {
        return this._validatorUserId;
    }

    /**
     * The last validation date of the Reference
     */
    protected _validationTime : NDate = null;
    get validationTime() : NDate {
        return this._validationTime;
    }

    /**
     * The valid from date of a History-Reference
     */
    readonly validFromTime : NDate = null;

    /**
     * The valid to date of a History-Reference
     */
    readonly validToTime : NDate = null;


    /*** ALIASES ***********************************************************************************************************************/

    get status() : Status {
        return metaModel[ this._schema ].getStatus( this._statusId )!;
    }

    /**
     * The the ReferenceSet of this Reference.
     */
    get referenceSet() {
        return metaModel[ this._schema ].getReferenceSet( this.referenceSetId )!;
    }

    get srcItem() : Item | undefined {
        let retVal;

        if ( this._dataContext ) {
            retVal = global.data[ this._dataContext ].items[ this._srcItemId ];
        }

        return retVal;
    }

    get destItem() : Item | undefined {
        let retVal;

        if ( this._dataContext ) {
            retVal = global.data[ this._dataContext ].items[ this._destItemId ];
        }

        return retVal;
    }


    /*************************************************************************************************************************************/

    /**
     * The database schema this Reference belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * The dataContext of the global data collection this Reference is stored in
     */
    protected _dataContext = '';
    get dataContext() {
        return this._dataContext;
    }

    /**
     * An map containing the EnumReferenceProperties by ReferencePropertyTypeId.
     */
    protected _enumProperties : Map<number, EnumReferenceProperty>;
    get enumProperties() {
        return this._enumProperties;
    }

    /**
     * An map containing the ReferenceProperties by ReferencePropertyTypeId.
     */
    protected _properties : Map<number, ReferenceProperty>;
    get properties() {
        return this._properties;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof ReferenceOnlyAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     * The constructor.
     *
     * @param attributes                An object containing attribute-value pairs for this
     * @param [dataContext='default']       The dataContext of the global data collection this belongs to
     * @param [schema=global.schema]    The database schema this belongs to
     * @param [dontRegister=false]      Set to true to avoid registration to global data collection
     */
    constructor( attributes : ReferenceAttributes, dataContext : string = 'default', schema : Schema = global.schema, dontRegister : boolean = false ) {

        if ( attributes.id && attributes.id < 1 ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        if ( attributes.referenceSetId === 0 ) {
            throw new LocalizableError( 'YN.base.dataObject.reference.attributes.validation.referenceSetId.isEmpty' );
        }

        if ( attributes.referenceSetId !== metaModel[ schema ].getReferenceSet( attributes.referenceSetId )?.id ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.attributes.validation.referenceSetId.invalid',
                { referenceSetId: String( attributes.referenceSetId ), schema }
            );
        }

        this._schema = schema;

        if ( attributes.id ) {
            this._id = attributes.id;
        }
        else {
            this._id = Reference.uniqueIdCount--;
        }

        this.referenceSetId = attributes.referenceSetId;
        this._srcItemId = attributes.srcItemId;
        this._destItemId = attributes.destItemId;

        this._properties = new Map();
        if ( attributes.properties ) {
            try {
                this.addPropertiesFromJSON( attributes.properties );
            }
            catch ( e ) {
                if ( e instanceof StackError ) {
///////////////////////////////////////////////
                    console.warn( e.toString() );
//////////////////////////////
///////////////////////////////////////////////
/////////////////////////////////////////
//////////////////////////////
                }
            }
        }

        this._enumProperties = new Map();
        if ( attributes.enumProperties ) {
            try {
                this.addEnumPropertiesFromJSON( attributes.enumProperties );
            }
            catch ( e ) {
                if ( e instanceof StackError ) {
///////////////////////////////////////////////
                    console.warn( e.toString() );
//////////////////////////////
///////////////////////////////////////////////
/////////////////////////////////////////
//////////////////////////////
                }
            }
        }

        if ( attributes.statusId ) {
            this._statusId = attributes.statusId;
        }
        else {
            this._statusId = metaModel[ schema ].getStatus( 'active' )!.id;
        }

        if ( attributes.modifierUserId ) {
            this._modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            if ( typeof attributes.modificationTime === 'string' ) {
                this._modificationTime = convertToDate( attributes.modificationTime );
            }
            else {
                this._modificationTime = attributes.modificationTime;
            }
        }
        if ( attributes.validatorUserId ) {
            this._validatorUserId = attributes.validatorUserId;
        }
        if ( attributes.validationTime ) {
            if ( typeof attributes.validationTime === 'string' ) {
                this._validationTime = convertToDate( attributes.validationTime );
            }
            else {
                this._validationTime = attributes.validationTime;
            }
        }
        if ( attributes.validFromTime ) {
            if ( typeof attributes.validFromTime === 'string' ) {
                this.validFromTime = convertToDate( attributes.validFromTime );
            }
            else {
                this.validFromTime = attributes.validFromTime;
            }
        }
        if ( attributes.validToTime ) {
            if ( typeof attributes.validToTime === 'string' ) {
                this.validToTime = convertToDate( attributes.validToTime );
            }
            else {
                this.validToTime = attributes.validToTime;
            }
        }

        this._attributesModified = new Map();

        if ( !dontRegister ) {
            this._dataContext = dataContext;
            this.registerToGlobalDataCollection();
        }
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param [includeAttributes='all']   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : ReferenceJsonAttributes {
        // required attributes
        const retVal : ReferenceJsonAttributes = {
                  id            : this._id,
                  referenceSetId: this.referenceSetId,
                  srcItemId     : this._srcItemId,
                  destItemId    : this._destItemId
              },
              isNew                            = this.isNew();

        if ( isNew || includeAttributes !== 'modified' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        // not settable attributes
        if ( includeAttributes !== 'modified' ) {
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = convertValueToString( this.modificationTime, 'TIMESTAMP' );
                retVal.validatorUserId = this.validatorUserId;
                retVal.validationTime = convertValueToString( this.validationTime, 'TIMESTAMP' );
            }

            // only history attributes
            else {
                retVal.validFromTime = convertValueToString( this.validFromTime, 'TIMESTAMP' );
                retVal.validToTime = convertValueToString( this.validToTime, 'TIMESTAMP' );
            }
        }

        if ( this._enumProperties.size ) {
            const enumProperties : ReferenceJsonAttributes[ 'enumProperties' ] = [];

            for ( const enumProperty of this._enumProperties.values() ) {
                if ( enumProperty.isNew() || enumProperty.isModified() || includeAttributes !== 'modified' ) {
                    enumProperties.push( enumProperty.toJSON( includeAttributes ) );
                }
            }

            if ( enumProperties.length ) {
                retVal.enumProperties = enumProperties;
            }
        }

        if ( this._properties.size ) {
            const properties : ReferenceJsonAttributes[ 'properties' ] = [];

            for ( const property of this._properties.values() ) {
                if ( property.isNew() || property.isModified() || includeAttributes !== 'modified' ) {
                    properties.push( property.toJSON( includeAttributes ) );
                }
            }

            if ( properties.length ) {
                retVal.properties = properties;
            }
        }

        return retVal;
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param [includeAttributes='all']   The state of the attributes to include in result.
     */
    toUpdateJSON( includeAttributes : 'all' | 'modified' = 'all' ) : ReferenceAttributesSettable {
        // required attributes
        const retVal : ReferenceAttributesSettable = {},
              isNew                                = this.isNew();

        if ( isNew || includeAttributes !== 'modified' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        if ( this._enumProperties.size ) {
            const enumProperties : ReferenceAttributesSettable[ 'enumProperties' ] = {};

            for ( const [ propertyTypeId, enumProperty ] of this._enumProperties.entries() ) {
                if ( enumProperty.isNew() || enumProperty.isModified() || includeAttributes !== 'modified' ) {
                    enumProperties[ propertyTypeId ] = enumProperty.toUpdateJSON( includeAttributes );
                }
            }

            if ( Object.keys( enumProperties ).length ) {
                ( retVal.enumProperties as { [ propertyTypeId : number ] : EnumReferencePropertyAttributesSettable } ) = enumProperties;
            }
        }

        if ( this._properties.size ) {
            const properties : ReferenceAttributesSettable[ 'properties' ] = {};

            for ( const [ propertyTypeId, property ] of this._properties.entries() ) {
                if ( property.isNew() || property.isModified() || includeAttributes !== 'modified' ) {
                    properties[ propertyTypeId ] = property.toUpdateJSON( includeAttributes );
                }
            }

            if ( Object.keys( properties ).length ) {
                retVal.properties = properties;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        let retVal = `Reference (ID: ${this._id}) '`,
            item;

        if ( this._dataContext !== '' ) {
            item = global.data[ this._dataContext ].items[ this._srcItemId ];

            if ( item ) {
                retVal += `${item.displayAttributeValue} (ID: ${this._srcItemId})`;
            }
            else {
                retVal += this._srcItemId;
            }

            retVal += ` ${__( 'YN.base.common.reference.directionMarker' )} ${this.referenceSet.referenceType.name} ${__( 'YN.base.common.reference.directionMarker' )} `;

            item = global.data[ this._dataContext ].items[ this._destItemId ];

            if ( item ) {
                retVal += `${item.displayAttributeValue} (ID: ${this._destItemId})`;
            }
            else {
                retVal += this._destItemId;
            }

            retVal += `'`;
        }

        return retVal;
    }

    /**
     * Returns a string containing the XML representation of this
     */
    toXML( indent : number = 0 ) : string {
        let retVal : string = '';

        if ( this.srcItem && this.destItem && this.referenceSetId ) {
            const propertySets = this.referenceSet.propertySets;

            const srcItemTypeName  = encodeHtmlEntities( this.srcItem.itemType.name, { level: 'xml' } ),
                  srcItemKey       = encodeHtmlEntities( this.srcItem.key, { level: 'xml' } ),
                  refType          = encodeHtmlEntities( this.referenceSet.referenceType.name, { level: 'xml' } ),
                  destItemTypeName = encodeHtmlEntities( this.destItem.itemType.name, { level: 'xml' } ),
                  destItemKey      = encodeHtmlEntities( this.destItem.key, { level: 'xml' } ),
                  statusName       = encodeHtmlEntities( this.status.name, { level: 'xml' } );
            retVal = `<reference src_type='${srcItemTypeName}' src_key='${srcItemKey}' type='${refType}' dest_type='${destItemTypeName}' dest_key='${destItemKey}' status='${statusName}'`.indent( indent );

            if ( !this._properties.size && !this._enumProperties.size ) {
                retVal += ' />';
            }
            else {
                retVal += '>\r\n';

                propertySets.sort( ( propertySet1, propertySet2 ) => {
                    if ( propertySet1.name && propertySet2.name ) {
                        if ( propertySet1.name > propertySet2.name ) {
                            return 1;
                        }
                        else if ( propertySet1.name < propertySet2.name ) {
                            return -1;
                        }
                    }

                    return 0;
                } );

                for ( const propertySet of propertySets ) {
                    const property = this._properties.get( propertySet.propertyTypeId )
                        ?? this._enumProperties.get( propertySet.propertyTypeId );

                    if ( property ) {
                        retVal += property.toXML( undefined, indent + 1 ) + '\r\n';
                    }
                }

                retVal += '</reference>'.indent( indent );
            }
        }

        return retVal;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() : Reference {
        if ( this._attributesModified.has( 'statusId' ) ) {
            this._statusId = this._attributesModified.get( 'statusId' ) as number;
        }

        // reset all EnumProperties
        for ( const enumProperty of this._enumProperties.values() ) {
            enumProperty.reset();

            // remove empty enum property
            if ( !enumProperty.properties.size ) {
                this._enumProperties.delete( enumProperty.referencePropertySet.propertyTypeId );
            }
        }

        // reset all Properties
        for ( const property of this._properties.values() ) {
            // remove new property
            if ( property.isNew() ) {
                this._properties.delete( property.referencePropertySet.propertyTypeId );
            }
            // reset existing property
            else {
                property.reset();
            }
        }

        this._attributesModified = new Map();

        return this;
    }

    /**
     * Commits all changes of settable attributes.
     */
    commit( referenceMetaDataMapping? : ReferenceMetaDataMapping, referencePropertyMetaDataMapping? : ReferencePropertyMetaDataMapping, itemMetaDataMapping? : ItemMetaDataMapping ) : Reference {

        // update id and metaData if new and mapped one is given
        if ( referenceMetaDataMapping?.[ this._id ] ) {
            this.unregisterFromGlobalDataCollection( itemMetaDataMapping );
            this._modifierUserId = referenceMetaDataMapping[ this._id ].modifierUserId;
            this._modificationTime = convertToDate( referenceMetaDataMapping[ this._id ].modificationTime );
            this._validatorUserId = referenceMetaDataMapping[ this._id ].validatorUserId;
            this._validationTime = convertToDate( referenceMetaDataMapping[ this._id ].validationTime );

            //if ( this.isNew() ) {
            if ( itemMetaDataMapping?.[ this._srcItemId ]?.id ) {
                this._srcItemId = itemMetaDataMapping[ this._srcItemId ].id;
            }
            if ( itemMetaDataMapping?.[ this._destItemId ]?.id ) {
                this._destItemId = itemMetaDataMapping[ this._destItemId ].id;
            }

            this._id = referenceMetaDataMapping[ this._id ].id;
            //}


            this.registerToGlobalDataCollection();
        }

        this._attributesModified = new Map();

        // commit all EnumProperties
        for ( const enumProperty of this._enumProperties.values() ) {
            enumProperty.commit( referenceMetaDataMapping, referencePropertyMetaDataMapping );
        }

        // commit all Properties
        for ( const property of this._properties.values() ) {
            property.commit( referencePropertyMetaDataMapping );
        }

        return this;
    }

    /**
     * Resets the given settable attribute to its initial value.
     *
     * @param {keyof ReferenceOnlyAttributesSettable} attribute
     */
    resetAttribute( attribute : keyof ReferenceOnlyAttributesSettable ) {
        if ( this._attributesModified.has( attribute ) ) {
            switch ( attribute ) {
                case 'statusId':
                    this._statusId = this._attributesModified.get( 'statusId' ) as number;
                    break;
            }

            this._attributesModified.delete( attribute );
        }
    }

    /**
     * Deletes this by removing it from global data collection
     *
     */
    delete() {
        if ( this._dataContext !== '' ) {
            this.unregisterFromGlobalDataCollection();
        }
    }

    /**
     * Apply given attributes to this item.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     * @param [override]            true to override attributes and existing (enum-) properties, false to merge them (means to keep modified values)
     * @param [clone]               true/false to use clone/update functions of properties and enum properties
     * @param [cloneDataContext]    The dataContext the clone is registered to
     */
    update( attributes : ReferenceAttributesSettable = {}, override : boolean = false, clone : boolean = false, cloneDataContext? : string ) : Reference {
        const deletedStatusIds = metaModel[ this._schema ].getDeletedStatus().map( s => s.id ),
              cloneOrUpdate    = clone ? 'clone' : 'update';

        // apply data
        if ( attributes.statusId !== undefined && attributes.statusId !== this._statusId && ( override || !this.isAttributeModified( 'statusId' ) ) ) {
            this.statusId = attributes.statusId;
        }

        // handle enumerated properties
        if ( attributes.enumProperties ) {
            for ( const enumPropertyTypeIdString of Object.keys( attributes.enumProperties ) ) {
                const enumPropertyAttributes = attributes.enumProperties[ enumPropertyTypeIdString ],
                      enumPropertyTypeId     = Number( enumPropertyTypeIdString ),
                      enumProperty           = this._enumProperties.get( enumPropertyTypeId );

                // add enumerated property
                if ( !enumProperty ) {
                    const referencePropertySet = metaModel[ this._schema ].getReferencePropertySetForReferenceSetAndPropertyType( this.referenceSetId, enumPropertyTypeId );

                    this.addEnumPropertyFromJSON( {
                        referenceId           : this._id,
                        referencePropertySetId: referencePropertySet?.id,
                        ...enumPropertyAttributes,
                        properties: undefined
                    } as EnumReferencePropertyAttributes );

                    this._enumProperties.set( enumPropertyTypeId, this._enumProperties.get( enumPropertyTypeId )![ cloneOrUpdate ]( enumPropertyAttributes ) );

                    // remove empty enumerated property
                    if ( !this._enumProperties.get( enumPropertyTypeId )?.properties.size ) {
                        this._enumProperties.delete( enumPropertyTypeId );
                    }
                }

                // normal enumerated property update
                else if ( override || !enumProperty.isModified() ) {
                    this._enumProperties.set( enumPropertyTypeId, enumProperty[ cloneOrUpdate ]( enumPropertyAttributes ) );

                    // remove empty enumerated property
                    if ( !this._enumProperties.get( enumPropertyTypeId )?.properties.size ) {
                        this._enumProperties.delete( enumPropertyTypeId );
                    }
                }
            }
        }

        // handle properties
        if ( attributes.properties ) {
            for ( const propertyTypeIdString of Object.keys( attributes.properties ) ) {
                const propertyAttributes = attributes.properties[ propertyTypeIdString ],
                      propertyTypeId     = Number( propertyTypeIdString ),
                      property           = this._properties.get( propertyTypeId );

                // add property
                if ( !property && ( propertyAttributes[ 'statusId' ] === undefined || !deletedStatusIds.includes( propertyAttributes[ 'statusId' ] ) ) ) {
                    const referencePropertySet = metaModel[ this._schema ].getReferencePropertySetForReferenceSetAndPropertyType( this.referenceSetId, propertyTypeId );

                    this.addPropertyFromJSON( {
                        referenceId           : this._id,
                        referencePropertySetId: referencePropertySet?.id,
                        ...propertyAttributes
                    } as ReferencePropertyAttributes );
                }

                else if ( property && ( override || !property.isModified() ) ) {

                    // delete property
                    if (
                        propertyAttributes[ 'statusId' ] !== undefined
                        && !deletedStatusIds.includes( property.statusId )
                        && deletedStatusIds.includes( propertyAttributes[ 'statusId' ] )
                    ) {
                        // remove new property
                        if ( property.isNew() ) {
                            this._properties.delete( propertyTypeId );
                        }

                        // delete existing property
                        else {
                            this._properties.set( propertyTypeId, property.reset()[ cloneOrUpdate ]( { statusId: propertyAttributes[ 'statusId' ] } ) );
                        }
                    }

                    // normal property update
                    else {
                        this._properties.set( propertyTypeId, property[ cloneOrUpdate ]( propertyAttributes ) );
                    }
                }
            }
        }

        if ( clone && cloneDataContext ) {
            this._dataContext = cloneDataContext;
        }

        return this;
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     * This old object will be unregistered and the new clone registered to the GlobalDataCollection
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     * @param [override]            true to override attributes and existing (enum-) properties, false to merge them (means to keep modified values)
     * @param [cloneDataContext]    The dataContext the clone is registered to
     */
    clone( attributes : ReferenceAttributesSettable = {}, override : boolean = false, cloneDataContext? : string ) : Reference {
        // unregister if
        if ( this._dataContext !== '' && !cloneDataContext ) {
            this.unregisterFromGlobalDataCollection();
        }

        const clone : Reference = omniclone(
            this,
            {
                invokeConstructors: false,
                setPrototype      : true,
                copyGettersSetters: true
            },
            ( node : any ) => {
                // do not clone (enum)properties - it's done in update
                if ( node instanceof ReferenceProperty || node instanceof EnumReferenceProperty ) {
                    return node;
                }
                else {
                    return undefined;
                }
            }
        );

        // clone and update parent in (enum)properties
        for ( const propertyTypeId of clone.enumProperties.keys() ) {
            const enumProperty = clone.enumProperties.get( propertyTypeId )!.clone();

            enumProperty.parent = clone;

            clone.enumProperties.set( propertyTypeId, enumProperty );
        }
        for ( const propertyTypeId of clone.properties.keys() ) {
            const property = clone.properties.get( propertyTypeId )!.clone();

            property.parent = clone;

            clone.properties.set( propertyTypeId, property );
        }

        // apply data
        try {
            clone.update( attributes, override, true, cloneDataContext );

            // register cloned reference if
            if ( clone.dataContext !== '' ) {
                clone.registerToGlobalDataCollection();
            }

            return clone;
        }
        catch ( e ) {
            // re-register original reference if
            if ( this._dataContext !== '' && !cloneDataContext ) {
                this.registerToGlobalDataCollection();
            }

            throw ( e );
        }
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this._id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this.validFromTime !== null;
    }

    /**
     * Checks if this is deleted.
     */
    isDeleted() : boolean {
        return metaModel[ this._schema ].getDeletedStatus().map( s => s.id ).includes( this._statusId );
    }

    /**
     * Checks if this or an EnumProperty or a Property of this is modified.
     */
    isModified() : boolean {
        let retVal = this._attributesModified.size > 0;

        // check if an EnumProperty is modified
        if ( !retVal && this._enumProperties.size ) {
            for ( const enumProperty of this._enumProperties.values() ) {
                if ( enumProperty.isModified() || enumProperty.isNew() ) {
                    retVal = true;
                    break;
                }
            }
        }

        // check if a Property is modified
        if ( !retVal && this._properties.size ) {
            for ( const property of this._properties.values() ) {
                if ( property.isModified() || property.isNew() ) {
                    retVal = true;
                    break;
                }
            }
        }

        return retVal;
    }

    /**
     * Checks if this and all EnumProperties and all Properties of this are valid.
     */
    isValid() : boolean {
        let retVal = true;

        // new and deleted
        if ( this.isNew() && this.isDeleted() ) {
            retVal = false;
        }

        // check if EnumProperties are valid
        if ( retVal && this._enumProperties.size ) {
            for ( const enumProperty of this._enumProperties.values() ) {
                if ( !enumProperty.isValid() ) {
                    retVal = false;
                    break;
                }
            }
        }

        // check if Properties are valid
        if ( retVal && this._properties.size ) {
            for ( const property of this._properties.values() ) {
                if ( !property.isValid() ) {
                    retVal = false;
                    break;
                }
            }
        }

        return retVal;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof ReferenceOnlyAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    /**
     * Gets if the (original) unmodified value for given attribute is modified .
     *
     * @param attribute        The attribute to get value for
     */
    getAttributeOriginalValue( attribute : keyof ReferenceOnlyAttributesSettable ) : AttributeValue {
        return this._attributesModified.get( attribute ) ?? this[ attribute ];
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof ReferenceOnlyAttributesSettable, value : AttributeValue ) {
        // set as modified
        if ( !this._attributesModified.has( attribute ) && value !== this[ attribute ] ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && value === this._attributesModified.get( attribute ) ) {
            this._attributesModified.delete( attribute );
        }
    }

    /**
     * Validates the given statusId
     */
    protected validateStatusId( statusId : number ) {
        if ( !metaModel[ this._schema ].getStatus( statusId ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.statusId.notExists' );
        }
    }

    /**
     * Registers this to the global data collection.
     */
    protected registerToGlobalDataCollection() {
        if ( !global.data[ this._dataContext ] ) {
            global.data[ this._dataContext ] = {
                items     : {},
                references: {},
                schema    : this._schema
            };
        }

        global.data[ this._dataContext ].references[ this._id ] = this;

        this.srcItem?.registerReference( this );
        this.destItem?.registerReference( this );
    }

    /**
     * Un-registers this from the global data collection.
     */
    protected unregisterFromGlobalDataCollection( itemMetaDataMapping? : ItemMetaDataMapping ) {
        if ( global.data[ this._dataContext ]?.references[ this._id ] ) {
            delete global.data[ this._dataContext ].references[ this._id ];

            // unregister current items
            this.srcItem?.unregisterReference( this );
            this.destItem?.unregisterReference( this );

            // unregister new items
            if ( itemMetaDataMapping?.[ this._srcItemId ] && global.data[ this.dataContext ].items[ itemMetaDataMapping[ this._srcItemId ].id ] ) {
                global.data[ this.dataContext ].items[ itemMetaDataMapping[ this._srcItemId ].id ].unregisterReference( this );
            }
            if ( itemMetaDataMapping?.[ this._destItemId ] && global.data[ this.dataContext ].items[ itemMetaDataMapping[ this._destItemId ].id ] ) {
                global.data[ this.dataContext ].items[ itemMetaDataMapping[ this._destItemId ].id ].unregisterReference( this );
            }
        }
    }


    /*** PROPERTIES RELATED **************************************************************************************************************/

    /**
     * Adds Properties given by data to this.
     *
     * @param dataObject            An array of propertyData
     */
    protected addPropertiesFromJSON( dataObject : ( Omit<ReferencePropertyAttributes, 'referenceId'> & Partial<Pick<ReferencePropertyAttributes, 'referenceId'>> )[] ) {
        const stackError = new StackError(
            'YN.base.dataObject.reference.addPropertiesFromJSON.error.title',
            { reference: this.toString() }
        );

        for ( const propertyData of dataObject ) {
            try {
                this.addPropertyFromJSON( propertyData );
            }
            catch ( addError ) {
                const referencePropertySet = metaModel[ this._schema ].getReferencePropertySet( propertyData.referencePropertySetId ),
                      subStackError        = new StackError(
                          'YN.base.dataObject.reference.addProperty.error.title',
                          { propertyTypeName: referencePropertySet?.name ?? 'n/a' }
                      );

                stackError.add( subStackError );
                subStackError.add( addError );
            }
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError );
        }
    }

    /**
     * Adds or updates Properties given by data to this.
     *
     * @param dataObject            An array of propertyData
     */
    protected addPropertyFromJSON( dataObject : Omit<ReferencePropertyAttributes, 'referenceId'> & Partial<Pick<ReferencePropertyAttributes, 'referenceId'>> ) : ReferenceProperty {
        return this.addOrUpdatePropertyFromJSON( dataObject, false );
    }

    /**
     * Adds or updates Properties given by data to this.
     *
     * @param dataObject            An array of propertyData
     */
    protected updatePropertyFromJSON( dataObject : Omit<ReferencePropertyAttributes, 'referenceId'> & Partial<Pick<ReferencePropertyAttributes, 'referenceId'>> ) : ReferenceProperty {
        return this.addOrUpdatePropertyFromJSON( dataObject, true );
    }

    /**
     * Adds a Property given by data to this.
     *
     * @param dataObject            An object containing attribute-value pairs
     * @param allowUpdates          true to allow updates, if false
     */
    private addOrUpdatePropertyFromJSON( dataObject : Omit<ReferencePropertyAttributes, 'referenceId'> & Partial<Pick<ReferencePropertyAttributes, 'referenceId'>>, allowUpdates : boolean = false ) : ReferenceProperty {
        let retVal : ReferenceProperty;
        const referencePropertySet = metaModel[ this._schema ].getReferencePropertySet( dataObject.referencePropertySetId );

        if ( !referencePropertySet || referencePropertySet.referenceSetId !== this.referenceSetId ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.noRPSet',
                { propertyTypeName: referencePropertySet?.name ?? `ReferencePropertySetId: ${dataObject.referencePropertySetId}`, referenceSetName: referencePropertySet?.referenceSet.name ?? `ReferencePropertySetId: ${dataObject.referencePropertySetId}` }
            );
        }

        if ( !allowUpdates && this._properties.has( referencePropertySet.propertyTypeId ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.exists',
                { propertyTypeName: referencePropertySet.name, referenceId: String( this._id ) }
            );
        }

        if ( referencePropertySet.isEnumerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.isEnumerated',
                { propertyTypeName: referencePropertySet.name, referenceSetName: referencePropertySet.referenceSet.name }
            );
        }

        if ( this._properties.has( referencePropertySet.propertyTypeId ) ) {
            retVal = this._properties.get( referencePropertySet.propertyTypeId )!.update( dataObject );
        }
        else {
            retVal = new ReferenceProperty( this, { ...dataObject, referenceId: this._id } );
            this._properties.set( referencePropertySet.propertyTypeId, retVal );
        }

        return retVal;
    }

    /**
     * Adds a new Property by PropertyType to this.
     *
     * @param propertyTypeId {Number}
     */
    addProperty( propertyTypeId : number ) : ReferenceProperty {
        if ( this._properties.has( propertyTypeId ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.exists',
                { propertyTypeName: this._properties.get( propertyTypeId )!.referencePropertySet.name, referenceId: String( this._id ) }
            );
        }

        const referencePropertySet = metaModel[ this._schema ].getReferencePropertySetForReferenceSetAndPropertyType( this.referenceSetId, propertyTypeId );

        if ( !referencePropertySet || referencePropertySet.referenceSetId !== this.referenceSetId ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.noRPSet',
                { propertyTypeName: referencePropertySet?.name ?? `ReferenceSetId: ${this.referenceSetId}, PropertyTypeId: ${propertyTypeId}`, referenceSetName: referencePropertySet?.referenceSet.name ?? `ReferenceSetId: ${this.referenceSetId}, PropertyTypeId: ${propertyTypeId}` }
            );
        }

        if ( referencePropertySet.isEnumerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.isEnumerated',
                { propertyTypeName: referencePropertySet.name, referenceSetName: referencePropertySet.referenceSet.name }
            );
        }

        const newReferenceProperty = new ReferenceProperty( this, { referenceId: this._id, referencePropertySetId: referencePropertySet.id } );

        this._properties.set( propertyTypeId, newReferenceProperty );

        return newReferenceProperty;
    }


    /*** ENUM PROPERTIES RELATED *********************************************************************************************************/

    /**
     * Adds EnumProperties given by data to this.
     *
     * @param dataObject            An array of propertyData
     */
    protected addEnumPropertiesFromJSON( dataObject : ( Omit<EnumReferencePropertyAttributes, 'referenceId'> & Partial<Pick<EnumReferencePropertyAttributes, 'referenceId'>> )[] ) {
        const stackError = new StackError(
            'YN.base.dataObject.reference.addEnumPropertiesFromJSON.error.title',
            { reference: this.toString() }
        );

        for ( const propertyData of dataObject ) {
            try {
                this.addEnumPropertyFromJSON( propertyData );
            }
            catch ( addError ) {
                const referencePropertySet = metaModel[ this._schema ].getReferencePropertySet( propertyData.referencePropertySetId ),
                      subStackError        = new StackError(
                          'YN.base.dataObject.reference.addEnumProperty.error.title',
                          { propertyTypeName: referencePropertySet?.name ?? 'n/a' }
                      );

                stackError.add( subStackError );
                subStackError.add( addError );
            }
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError );
        }
    }

    /**
     * Adds a EnumProperty given by data to this.
     *
     * @param dataObject            An object containing attribute-value pairs
     */
    protected addEnumPropertyFromJSON( dataObject : Omit<EnumReferencePropertyAttributes, 'referenceId'> & Partial<Pick<EnumReferencePropertyAttributes, 'referenceId'>> ) : EnumReferenceProperty {
        return this.addOrUpdateEnumPropertyFromJSON( dataObject, false );
    }

    /**
     * Updates a EnumProperty given by data to this.
     *
     * @param dataObject            An object containing attribute-value pairs
     */
    protected updateEnumPropertyFromJSON( dataObject : Omit<EnumReferencePropertyAttributes, 'referenceId'> & Partial<Pick<EnumReferencePropertyAttributes, 'referenceId'>> ) : EnumReferenceProperty {
        return this.addOrUpdateEnumPropertyFromJSON( dataObject, true )
    }

    /**
     * Adds or updates a EnumProperty given by data to this.
     *
     * @param dataObject            An object containing attribute-value pairs
     * @param allowUpdates          true to allow updates, if false
     */
    private addOrUpdateEnumPropertyFromJSON( dataObject : Omit<EnumReferencePropertyAttributes, 'referenceId'> & Partial<Pick<EnumReferencePropertyAttributes, 'referenceId'>>, allowUpdates : boolean = false ) : EnumReferenceProperty {
        let retVal : EnumReferenceProperty;
        const referencePropertySet = metaModel[ this._schema ].getReferencePropertySet( dataObject.referencePropertySetId );

        if ( !referencePropertySet || referencePropertySet.referenceSetId !== this.referenceSetId ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.noRPSet',
                { propertyTypeName: referencePropertySet?.name ?? `ReferencePropertySetId: ${dataObject.referencePropertySetId}`, referenceSetName: referencePropertySet?.referenceSet.name ?? `ReferencePropertySetId: ${dataObject.referencePropertySetId}` }
            );
        }

        if ( !allowUpdates && this._enumProperties.has( referencePropertySet.propertyTypeId ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.exists',
                { propertyTypeName: referencePropertySet.name, referenceId: String( this._id ) }
            );
        }

        if ( !referencePropertySet.isEnumerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.isNotEnumerated',
                { propertyTypeName: referencePropertySet.name, referenceSetName: referencePropertySet.referenceSet.name }
            );
        }

        if ( this._properties.has( referencePropertySet.propertyTypeId ) ) {
            retVal = this._enumProperties.get( referencePropertySet.propertyTypeId )!.update( dataObject as EnumReferencePropertyAttributesSettable );
        }
        else {
            retVal = new EnumReferenceProperty( this, { ...dataObject, referenceId: this._id } );
            this._enumProperties.set( referencePropertySet.propertyTypeId, retVal );
        }

        return retVal;
    }

    /**
     * Adds a new EnumProperty by PropertyType to this.
     *
     * @param propertyTypeId
     */
    addEnumProperty( propertyTypeId : number ) : EnumReferenceProperty {
        if ( this._enumProperties.has( propertyTypeId ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.exists',
                { propertyTypeName: this._enumProperties.get( propertyTypeId )!.referencePropertySet.name, referenceId: String( this._id ) }
            );
        }

        const referencePropertySet = metaModel[ this._schema ].getReferencePropertySetForReferenceSetAndPropertyType( this.referenceSetId, propertyTypeId );

        if ( !referencePropertySet || referencePropertySet.referenceSetId !== this.referenceSetId ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.noRPSet',
                { propertyTypeName: referencePropertySet?.name ?? `ReferenceSetId: ${this.referenceSetId}, PropertyTypeId: ${propertyTypeId}`, referenceSetName: referencePropertySet?.referenceSet.name ?? `ReferenceSetId: ${this.referenceSetId}, PropertyTypeId: ${propertyTypeId}` }
            );
        }

        if ( !referencePropertySet.isEnumerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.reference.addProperty.isNotEnumerated',
                { propertyTypeName: referencePropertySet.name, referenceSetName: referencePropertySet.referenceSet.name }
            );
        }

        const newEnumReferenceProperty = new EnumReferenceProperty( this, { referenceId: this._id, referencePropertySetId: referencePropertySet.id } );

        this._enumProperties.set( propertyTypeId, newEnumReferenceProperty );

        return newEnumReferenceProperty;
    }


    /*** ITEM RELATED ********************************************************************************************************************/

    getOtherItem( itemId : number ) : Item | undefined {
        let retVal;

        if ( this._dataContext ) {
            if ( itemId === this._srcItemId ) {
                retVal = global.data[ this._dataContext ].items[ this._destItemId ];
            }
            else if ( itemId === this._destItemId ) {
                retVal = global.data[ this._dataContext ].items[ this._srcItemId ];
            }
        }

        return retVal;
    }

}

export default Reference;
